/* ==========================================================================
   Faja Fina · global.js
   Utilidades y componentes comunes (sin dependencias):
   cabecera fija, menú móvil, modales, marquesina, sliders, cuenta atrás,
   popup, volver arriba, animaciones de entrada y seguimiento de pedidos.
   ========================================================================== */
(() => {
  const FF = (window.FF = window.FF || {});
  FF.strings = FF.strings || {};

  /* ---------- Utilidades ---------- */
  FF.debounce = (fn, wait = 200) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  FF.escapeHtml = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  /** Formatea céntimos con el formato de moneda de la tienda (igual que el filtro money de Shopify). */
  FF.formatMoney = (cents, format) => {
    const moneyFormat = format || FF.moneyFormat || '{{amount_with_comma_separator}} €';
    const value = Number(typeof cents === 'string' ? cents.replace('.', '') : cents) || 0;
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const toFixed = (amount, precision, thousands = ',', decimal = '.') => {
      const [int, dec] = (amount / 100).toFixed(precision).split('.');
      const withSep = int.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return dec ? `${withSep}${decimal}${dec}` : withSep;
    };
    const match = moneyFormat.match(pattern);
    if (!match) return toFixed(value, 2);
    const formatters = {
      amount: () => toFixed(value, 2),
      amount_no_decimals: () => toFixed(value, 0),
      amount_with_comma_separator: () => toFixed(value, 2, '.', ','),
      amount_no_decimals_with_comma_separator: () => toFixed(value, 0, '.', ','),
      amount_with_apostrophe_separator: () => toFixed(value, 2, "'", '.'),
      amount_no_decimals_with_space_separator: () => toFixed(value, 0, ' '),
      amount_with_space_separator: () => toFixed(value, 2, ' ', ','),
      amount_with_period_and_space_separator: () => toFixed(value, 2, ' ', '.'),
    };
    return moneyFormat.replace(pattern, (formatters[match[1]] || formatters.amount)());
  };

  FF.swatchColor = (name) => {
    if (!name || !FF.swatches) return null;
    return FF.swatches[String(name).trim().toLowerCase()] || null;
  };

  FF.isColorOption = (name) =>
    (FF.colorOptionNames || ['color', 'colour']).map((n) => String(n).trim()).includes(String(name).trim().toLowerCase());

  /* ---------- Foco y scroll ---------- */
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  FF.trapFocus = (container, elementToFocus) => {
    FF.removeTrapFocus();
    FF._trap = (event) => {
      if (event.key !== 'Tab') return;
      const nodes = [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetWidth || el.offsetHeight || el === document.activeElement);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', FF._trap);
    const target = elementToFocus || container;
    requestAnimationFrame(() => target && target.focus({ preventScroll: true }));
  };

  FF.removeTrapFocus = (elementToFocus) => {
    if (FF._trap) document.removeEventListener('keydown', FF._trap);
    FF._trap = null;
    if (elementToFocus && typeof elementToFocus.focus === 'function') elementToFocus.focus({ preventScroll: true });
  };

  let scrollLocks = 0;
  FF.lockScroll = () => {
    scrollLocks += 1;
    document.body.classList.add('is-locked');
  };
  FF.unlockScroll = () => {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (!scrollLocks) document.body.classList.remove('is-locked');
  };

  /* Pila de capas abiertas: Escape cierra siempre la de arriba, tenga el foco donde lo tenga */
  const escapeStack = [];
  FF.pushEscape = (close) => {
    escapeStack.push(close);
  };
  FF.popEscape = (close) => {
    const index = escapeStack.lastIndexOf(close);
    if (index > -1) escapeStack.splice(index, 1);
  };
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !escapeStack.length) return;
    event.preventDefault();
    escapeStack[escapeStack.length - 1]();
  });

  FF.scrollToTarget = (selector) => {
    const target = selector && document.querySelector(selector);
    if (!target) return false;
    const header = document.querySelector('.section-header');
    const offset = (header ? header.offsetHeight : 0) + 14;
    window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' });
    return true;
  };

  FF.toast = (message, duration = 2800) => {
    const el = document.querySelector('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(FF._toastTimer);
    FF._toastTimer = setTimeout(() => {
      el.hidden = true;
    }, duration);
  };

  /* ---------- Capa superpuesta base (menú, carrito, modales) ---------- */
  class OverlayElement extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[role="dialog"]');
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close], [data-modal-close]')) {
          event.preventDefault();
          this.close();
        }
      });
      this.closeHandler = () => this.close();
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(opener) {
      if (this.isOpen) return;
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      FF.lockScroll();
      FF.trapFocus(this, this.panel);
      FF.pushEscape(this.closeHandler);
      this.dispatchEvent(new CustomEvent('overlay:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      FF.unlockScroll();
      FF.popEscape(this.closeHandler);
      FF.removeTrapFocus(this.opener);
      if (this.opener && this.opener.hasAttribute('aria-expanded')) this.opener.setAttribute('aria-expanded', 'false');
      this.dispatchEvent(new CustomEvent('overlay:close', { bubbles: true }));
    }
  }
  FF.OverlayElement = OverlayElement;

  customElements.define('menu-drawer', class MenuDrawer extends OverlayElement {});
  customElements.define('modal-dialog', class ModalDialog extends OverlayElement {});

  /* ---------- Cabecera fija ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this;
      this.mode = this.dataset.sticky || 'on-scroll-up';
      if (this.mode === 'none') {
        this.section.classList.add('is-static');
        return;
      }
      this.lastY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      const y = window.scrollY;
      const height = this.section.offsetHeight;
      this.section.classList.toggle('is-scrolled', y > 8);
      if (this.mode === 'on-scroll-up') {
        if (document.body.classList.contains('is-locked')) return;
        if (y > this.lastY + 4 && y > height * 2.5) this.section.classList.add('is-hidden');
        else if (y < this.lastY - 4 || y <= height) this.section.classList.remove('is-hidden');
      }
      this.lastY = y;
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- Marquesina infinita ---------- */
  class MarqueeStrip extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-marquee-track]');
      this.group = this.querySelector('[data-marquee-group]');
      if (!this.track || !this.group) return;
      this.originals = [...this.group.children];
      this.build();
      if ('ResizeObserver' in window) {
        let lastWidth = this.offsetWidth;
        this.resizeObserver = new ResizeObserver(
          FF.debounce(() => {
            if (Math.abs(this.offsetWidth - lastWidth) < 40) return;
            lastWidth = this.offsetWidth;
            this.build();
          }, 200)
        );
        this.resizeObserver.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    build() {
      this.track.querySelectorAll('[data-marquee-clone]').forEach((node) => node.remove());
      this.group.querySelectorAll('[data-marquee-fill]').forEach((node) => node.remove());
      const target = Math.max(this.offsetWidth, window.innerWidth);
      let guard = 0;
      while (this.group.scrollWidth < target && guard < 12) {
        this.originals.forEach((node) => {
          const copy = node.cloneNode(true);
          copy.setAttribute('data-marquee-fill', '');
          copy.setAttribute('aria-hidden', 'true');
          copy.removeAttribute('data-shopify-editor-block');
          this.group.appendChild(copy);
        });
        guard += 1;
      }
      const clone = this.group.cloneNode(true);
      clone.removeAttribute('data-marquee-group');
      clone.setAttribute('data-marquee-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((n) => n.removeAttribute('data-shopify-editor-block'));
      clone.querySelectorAll('a, button').forEach((n) => n.setAttribute('tabindex', '-1'));
      this.track.appendChild(clone);
    }
  }
  customElements.define('marquee-strip', MarqueeStrip);

  /* ---------- Slider (scroll-snap nativo + flechas + puntos) ---------- */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      if (!this.track) return;
      this.prevButtons = [...this.querySelectorAll('[data-slider-prev]')];
      this.nextButtons = [...this.querySelectorAll('[data-slider-next]')];
      this.dots = this.querySelector('[data-slider-dots]');
      this.prevButtons.forEach((b) => b.addEventListener('click', () => this.step(-1)));
      this.nextButtons.forEach((b) => b.addEventListener('click', () => this.step(1)));
      this.track.addEventListener('scroll', FF.debounce(() => this.update(), 50), { passive: true });
      this.onResize = FF.debounce(() => this.refresh(), 150);
      window.addEventListener('resize', this.onResize);
      this.refresh();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
    }

    get slides() {
      return [...this.track.children].filter((el) => el.offsetParent !== null);
    }

    get gap() {
      const style = getComputedStyle(this.track);
      return parseFloat(style.columnGap || style.gap) || 0;
    }

    get isScrollable() {
      return this.track.scrollWidth > this.track.clientWidth + 4;
    }

    slideWidth() {
      const first = this.slides[0];
      return first ? first.offsetWidth + this.gap : this.track.clientWidth;
    }

    pageCount() {
      const width = this.slideWidth();
      if (!width) return 1;
      const visible = Math.max(1, Math.round((this.track.clientWidth + this.gap) / width));
      return Math.max(1, this.slides.length - visible + 1);
    }

    currentIndex() {
      return Math.round(this.track.scrollLeft / this.slideWidth());
    }

    step(direction) {
      this.track.scrollBy({ left: direction * this.slideWidth(), behavior: 'smooth' });
    }

    goTo(index) {
      this.track.scrollTo({ left: index * this.slideWidth(), behavior: 'smooth' });
    }

    refresh() {
      const active = this.isScrollable;
      this.toggleAttribute('data-inactive', !active);
      if (this.dots) {
        const count = active ? this.pageCount() : 0;
        if (this.dots.children.length !== count) {
          this.dots.innerHTML = '';
          for (let i = 0; i < count; i += 1) {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'slider__dot';
            dot.tabIndex = -1;
            dot.addEventListener('click', () => this.goTo(i));
            this.dots.appendChild(dot);
          }
        }
      }
      this.update();
    }

    update() {
      const index = Math.min(this.currentIndex(), this.pageCount() - 1);
      if (this.dots) [...this.dots.children].forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      const atStart = this.track.scrollLeft <= 4;
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 4;
      this.prevButtons.forEach((b) => (b.disabled = atStart));
      this.nextButtons.forEach((b) => (b.disabled = atEnd));
      const controls = this.querySelector('.slider__controls');
      if (controls) controls.hidden = !this.isScrollable;
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* ---------- Cantidad (+ / −) ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (event) => {
        const minus = event.target.closest('[data-qty-minus]');
        const plus = event.target.closest('[data-qty-plus]');
        if (!minus && !plus) return;
        const min = Number(this.input.min || 1);
        const next = Math.max(min, Number(this.input.value || min) + (plus ? 1 : -1));
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Cuenta atrás ligada a una fecha real ---------- */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      this.end = Date.parse(this.dataset.end);
      if (Number.isNaN(this.end)) {
        this.hidden = true;
        return;
      }
      this.parts = {};
      this.querySelectorAll('[data-cd]').forEach((el) => {
        this.parts[el.dataset.cd] = el;
      });
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const diff = this.end - Date.now();
      if (diff <= 0) {
        clearInterval(this.timer);
        this.hidden = true;
        return;
      }
      const total = Math.floor(diff / 1000);
      const values = {
        days: Math.floor(total / 86400),
        hours: Math.floor((total % 86400) / 3600),
        minutes: Math.floor((total % 3600) / 60),
        seconds: total % 60,
      };
      Object.entries(values).forEach(([key, val]) => {
        if (this.parts[key]) this.parts[key].textContent = String(val).padStart(2, '0');
      });
      const dayUnit = this.parts.days && this.parts.days.closest('.countdown__unit');
      if (dayUnit) {
        const hideDays = values.days === 0;
        dayUnit.hidden = hideDays;
        if (dayUnit.nextElementSibling) dayUnit.nextElementSibling.hidden = hideDays;
      }
    }
  }
  customElements.define('countdown-timer', CountdownTimer);

  /* ---------- Botón volver arriba ---------- */
  class ScrollTop extends HTMLElement {
    connectedCallback() {
      this.hidden = false;
      this.querySelector('button').addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
      this.onScroll = () => this.classList.toggle('is-visible', window.scrollY > 800);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
  }
  customElements.define('scroll-top', ScrollTop);

  /* ---------- Popup de bienvenida ---------- */
  class PromoPopup extends HTMLElement {
    connectedCallback() {
      this.key = `ff-popup-${this.dataset.sectionId}`;
      this.dialog = this.querySelector('[role="dialog"]');
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-popup-close]')) {
          this.close();
          return;
        }
        const cta = event.target.closest('[data-popup-cta]');
        if (cta && cta.dataset.scrollTarget) {
          event.preventDefault();
          this.close();
          setTimeout(() => FF.scrollToTarget(cta.dataset.scrollTarget), 200);
        }
      });
      this.closeHandler = () => this.close();

      if (this.dataset.designMode === 'true') {
        document.addEventListener('shopify:section:select', (event) => {
          if (event.detail.sectionId === this.dataset.sectionId) this.open(true);
        });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.detail.sectionId === this.dataset.sectionId) this.close();
        });
        return;
      }

      if (this.querySelector('.promo-popup__success')) {
        this.open(true);
        return;
      }
      if (this.wasSeen()) return;

      const delay = Number(this.dataset.delay || 0) * 1000;
      this.timer = setTimeout(() => this.open(), delay);

      if (this.dataset.exitIntent === 'true' && window.matchMedia('(pointer: fine)').matches) {
        this.onLeave = (event) => {
          if (!event.relatedTarget && event.clientY <= 4) this.open();
        };
        document.addEventListener('mouseout', this.onLeave);
      }
    }

    wasSeen() {
      try {
        const days = Number(this.dataset.frequency || 0);
        if (days === 0) return window.sessionStorage.getItem(this.key) === '1';
        const seenAt = Number(window.localStorage.getItem(this.key) || 0);
        return seenAt > 0 && Date.now() - seenAt < days * 86400000;
      } catch (error) {
        return false;
      }
    }

    remember() {
      try {
        const days = Number(this.dataset.frequency || 0);
        if (days === 0) window.sessionStorage.setItem(this.key, '1');
        else window.localStorage.setItem(this.key, String(Date.now()));
      } catch (error) {
        /* almacenamiento no disponible: el popup se mostrará en cada visita */
      }
    }

    open(force = false) {
      if (this.isOpen) return;
      if (!force && document.body.classList.contains('is-locked')) {
        this.timer = setTimeout(() => this.open(), 4000);
        return;
      }
      clearTimeout(this.timer);
      if (this.onLeave) document.removeEventListener('mouseout', this.onLeave);
      this.hidden = false;
      this.isOpen = true;
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
      FF.lockScroll();
      FF.trapFocus(this, this.dialog);
      FF.pushEscape(this.closeHandler);
      this.remember();
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      FF.unlockScroll();
      FF.popEscape(this.closeHandler);
      FF.removeTrapFocus();
      setTimeout(() => {
        if (!this.isOpen) this.hidden = true;
      }, 380);
    }
  }
  customElements.define('promo-popup', PromoPopup);

  /* ---------- Seguimiento de pedido ---------- */
  class TrackingForm extends HTMLElement {
    connectedCallback() {
      const form = this.querySelector('form');
      const input = this.querySelector('input');
      const error = this.querySelector('[data-tracking-error]');
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const value = input.value.trim().replace(/\s+/g, '');
        if (value.length < 6) {
          error.hidden = false;
          input.focus();
          return;
        }
        error.hidden = true;
        const template = this.dataset.url || 'https://t.17track.net/es#nums=[numero]';
        window.open(template.replace('[numero]', encodeURIComponent(value)), '_blank', 'noopener');
      });
    }
  }
  customElements.define('tracking-form', TrackingForm);

  /* ---------- Animaciones de entrada ---------- */
  const initReveal = (root = document) => {
    const nodes = root.querySelectorAll('[data-reveal]:not(.is-visible)');
    if (!document.body.classList.contains('has-reveal') || (window.Shopify && window.Shopify.designMode) || !('IntersectionObserver' in window)) {
      nodes.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.04 }
    );
    nodes.forEach((el) => observer.observe(el));
  };

  /* ---------- Vídeos que se reproducen al verse ---------- */
  const initVideos = (root = document) => {
    const wrappers = root.querySelectorAll('[data-autoplay-in-view]');
    if (!wrappers.length || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          const video = target.querySelector('video');
          if (!video) return;
          if (isIntersecting) {
            video.muted = true;
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.35 }
    );
    wrappers.forEach((wrapper) => observer.observe(wrapper));
  };

  /* ---------- Delegación de clics ---------- */
  document.addEventListener('click', (event) => {
    const menuOpener = event.target.closest('[data-menu-open]');
    if (menuOpener) {
      const drawer = document.querySelector('menu-drawer');
      if (drawer) {
        drawer.open(menuOpener);
        menuOpener.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    const modalOpener = event.target.closest('[data-modal-open]');
    if (modalOpener) {
      const modal = document.getElementById(modalOpener.dataset.modalOpen);
      if (modal) modal.open(modalOpener);
      return;
    }

    const scroller = event.target.closest('[data-scroll-target]');
    if (scroller && !scroller.closest('promo-popup')) {
      event.preventDefault();
      if (!FF.scrollToTarget(scroller.dataset.scrollTarget) && scroller.dataset.fallback) {
        window.location.href = scroller.dataset.fallback;
      }
    }
  });

  /* Acordeones: solo uno abierto por grupo */
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.open) return;
      const group = details.closest('[data-accordion-group="true"]');
      if (!group) return;
      group.querySelectorAll('details[open]').forEach((other) => {
        if (other !== details) other.open = false;
      });
    },
    true
  );

  /* Submenús del header al pasar el ratón */
  const initHoverMenus = () => {
    const canHover = window.matchMedia('(hover: hover)');
    document.querySelectorAll('[data-hover-details]').forEach((details) => {
      details.addEventListener('mouseenter', () => {
        if (canHover.matches) details.open = true;
      });
      details.addEventListener('mouseleave', () => {
        if (canHover.matches) details.open = false;
      });
    });
  };

  const init = () => {
    initReveal();
    initVideos();
    initHoverMenus();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  /* Editor de temas: reiniciar al recargar una sección */
  document.addEventListener('shopify:section:load', (event) => {
    initReveal(event.target);
    initVideos(event.target);
  });
})();
